<template>
    <div v-if="project" class="project-details">

      <div class="title">
        <h1>{{ project.title }}</h1>
        <h2>{{ project.title_2 }}, {{ project.date }}</h2>
      </div>

      <div class="content">

        <iframe v-if="project.youtube_video"
          :src="getYouTubeEmbedUrl(project.youtube_video)" 
          frameborder="0" 
          allowfullscreen>
        </iframe>

        <img v-else-if="project.image_card"
          class="image-card" 
          :src="getImagePath(project.image_card)" 
          alt="Main Project Image"
          @click="openImage(getImagePath(project.image_card))"
          style="cursor: zoom-in">

        <section class="right-column">

          <div class="links">
            <p v-if="project.link">
              <a :href="project.link" target="_blank">{{ project.link_name }}</a>
            </p>
            <p v-if="project.document">
              <a :href="getFilePath(project.document)" target="_blank">{{ project.document_name }}</a>
            </p>
          </div>

            <p v-html="formattedDescription"></p>

            <p class="infos" v-if="project.with.length">
              <strong>made with:<br></strong> {{ project.with.join(", ") }}
            </p>

            <p class="infos" v-if="project.software_langage.length">
              <strong>using:<br></strong> {{ project.software_langage.join(", ") }}
            </p>

        </section>
      </div>

      <div class="images-container">
        <img 
          v-for="(image, index) in filteredImages" 
          :key="index" 
          :src="getImagePath(image)" 
          alt="Project Image"
          @click="openImage(getImagePath(image))"
          style="cursor: zoom-in">
      </div>

      <div class="videos-container">
        <video 
          v-for="(video, index) in project.videos" 
          :key="index" 
          :src="getImagePath(video)" 
          alt="Project Video"
          autoplay loop muted>
        </video>
      </div>

    </div>

    <div v-if="enlargedImage" class="lightbox" @click="closeImage">
      <button class="prev-button" @click.stop="previousImage">&#8592;</button>
      <img :src="getImagePath(enlargedImage)" alt="Zoomed Image">
      <button class="next-button" @click.stop="nextImage">&#8594;</button>
    </div>
  </template>
  
  <script>
  import projectsData from '../projects.json';
  
  export default {
    props: ['referencement'],
    data() {
      return {
        enlargedImage: null, 
        enlargedIndex: null, 
        tempImageList: null,
      };
    },
    computed: {
      project() {
        return projectsData.find(proj => proj.referencement == this.referencement);
      },
      formattedDescription() {
      let desc = this.project.description;

      if (this.project.link_on_word) {
        this.project.link_on_word.forEach(link => {
          desc = desc.replace(
            new RegExp(`\\b${link.word}\\b`, "g"),
            `<a href='${link.url}' target='_blank'>${link.word}</a>`
          );
        });
      }

      return desc;
    },
    filteredImages() {
      if (this.project.youtube_video) {
        return this.project.images;
      }
      return this.project.images.filter(image => image !== this.project.image_card);
    }
    },
    
  methods: {
    getImagePath(image) {
      return `/projects_assets/${image}`;
    },
    getFilePath(file) {
      const fileExt = file.split("/").pop();
      if (fileExt.split(".").pop() === "pdf") return `/projects_assets/${file}`;
      return file
    },
    getYouTubeEmbedUrl(url) {
      if (url.includes('vimeo.com')) {
        const vimeoId = url.split('/').pop();
        return `https://player.vimeo.com/video/${vimeoId}`;
      }
      const videoId = url.split("v=")[1] || url.split("/").pop();
      return `https://www.youtube.com/embed/${videoId}`;
    },
    openImage(imagePath) {
      const allImages = [...(this.project.images || [])];
      if (
        this.project.image_card &&
        this.getImagePath(this.project.image_card) === imagePath &&
        !allImages.includes(this.project.image_card)
      ) {
        allImages.unshift(this.project.image_card);
      }
      const index = allImages.findIndex(img => this.getImagePath(img) === imagePath);

      if (index !== -1) {
        this.enlargedImage = allImages[index];
        this.enlargedIndex = index;
        this.tempImageList = allImages;
      } else {
        this.enlargedImage = imagePath.replace('/projects_assets/', '');
        this.enlargedIndex = null;
      }
    },
    closeImage() {
      this.enlargedImage = null;
      this.tempImageList = null;
    },
    nextImage() {
      const list = this.tempImageList || this.project.images;
      if (this.enlargedIndex !== null && list) {
        const nextIndex = (this.enlargedIndex + 1) % list.length;
        this.enlargedImage = list[nextIndex];
        this.enlargedIndex = nextIndex;
      }
    },
    previousImage() {
      const list = this.tempImageList || this.project.images;
      if (this.enlargedIndex !== null && list) {
        const prevIndex = (this.enlargedIndex - 1 + list.length) % list.length;
        this.enlargedImage = list[prevIndex];
        this.enlargedIndex = prevIndex;
      }
    },
    handleKeydown(event) {
      if (!this.enlargedImage) return;

      if (event.key === "ArrowRight") {
        this.nextImage();
      } else if (event.key === "ArrowLeft") {
        this.previousImage();
      } else if (event.key === "Escape") {
        this.closeImage();
      }
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeydown);
  },
  };
  </script>
  
  <style scoped>
  
  .project-details {
    text-align: left;
    margin: auto;
    padding-top: 40px;
    padding-left: 60px;
    padding-right: 60px;
    width: 70%;
    text-align: justify;
  }

  h1 {
    font-size: 28px;
    padding: 0;
    margin-bottom: 4px;
  }

  h2 {
    font-size: 16px;
    padding: 0;
  }

  .content {
    padding-top: 40px;
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 20px;
    align-items: flex-start;
  }

  .right-column {
    flex: 1; 
    display: flex;
    flex-direction: column;
  }

  .links {
    display: flex;
    justify-content: space-between;

    a {
      font-family: 'Avara Regular';
    }
  }

  .image-card {
    width: 100%;
    height: 100%; 
    max-width: 600px;
  }

  .images-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-top: 30px;
    margin-bottom: 30px;

    img {
      max-height: 300px;
      height: auto;
    }
  }

  img:hover, video:hover{
    box-shadow: 0 0 2px 1px var(--color-violet-clair);
  }

  .videos-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-top: 30px;
    margin-bottom: 30px;

    video {
      max-height: 300px;
      height: auto;
    }
  }

  /* a {
    display: block;
    margin-top: 10px;
    color: #007BFF;
    text-decoration: none;
    font-weight: bold;
  } */

  iframe {
    width: 100%;
    max-width: 600px;
    height: 315px;
    margin-top: 20px;
  }

  .lightbox {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(255, 232, 246, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    cursor: zoom-out;
  }

  .lightbox img {
    max-width: 90%;
    max-height: 90%;
    box-shadow: 0 0 5px #fff;
    transition: transform 0.3s ease;
  }

  .prev-button, .next-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    font-size: 2.5rem;
    color: #333;
    cursor: pointer;
    user-select: none;
  }

  .prev-button {
    left: 20px;
  }

  .next-button {
    right: 20px;
  }

  @media (max-width: 768px) {
    .content {
      flex-direction: column; 
    }
    .left-column, .right-column {
      max-width: 100%; 
    }

    .title {
      padding-top: 20px;
    }

    h1 {
      font-size: 24px;
    }

    .content {
      padding-top: 5px;
    }

    .project-details {
      width: 90%;
      padding: 0;
    }

    img {
        max-width: 100%;
        max-height: 100%;
    }

    iframe {
      max-width: 100%;
      height: auto;
    }

    .prev-button, .next-button {
      top: 90%;
    }
  }
  </style>
  